/**
 * ProviderList - Ordered list of recognition providers (Voter list) for one dice type.
 *
 * Any provider, LLM or non-LLM, can appear as a togglable row. Gated LLMs
 * (provider.requiresAuth === true) render as disabled rows with a sign-up CTA
 * for unauthenticated users; the backend silently strips gated LLMs from the
 * stored voter list for guests.
 *
 * Sorting:
 * - Active providers first, in their current priority order
 * - Inactive providers alphabetical by label
 */

import type {
  AvailableProvider,
  RecognitionProvider
} from '@dice-capture/shared/types/dice-registry';
import { ProviderListItem } from './ProviderListItem';

interface ProviderListProps {
  /** All available providers with training status */
  availableProviders: AvailableProvider[];
  /** Currently active (ordered) providers */
  activeProviders: RecognitionProvider[];
  /** Called with the new ordered list of active providers when user makes a change */
  onChange: (newActiveProviders: RecognitionProvider[]) => void;
}

/**
 * Build the display order for providers:
 * 1. Active providers in their current priority order
 * 2. Inactive providers (alphabetical by label)
 */
function buildDisplayOrder(
  availableProviders: AvailableProvider[],
  activeProviders: RecognitionProvider[]
): AvailableProvider[] {
  const activeSet = new Set(activeProviders);
  const providerMap = new Map(availableProviders.map((p) => [p.id, p]));

  const activeOrdered: AvailableProvider[] = activeProviders
    .map((id) => providerMap.get(id))
    .filter((p): p is AvailableProvider => p !== undefined);

  const inactive: AvailableProvider[] = availableProviders
    .filter((p) => !activeSet.has(p.id))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [...activeOrdered, ...inactive];
}

/**
 * Ordered list of providers with toggle and reorder controls.
 * Delegates row rendering to ProviderListItem.
 */
export function ProviderList({
  availableProviders,
  activeProviders,
  onChange
}: ProviderListProps) {
  // Guard: availableProviders may be undefined when bulk-loaded from /my-registrations
  if (!availableProviders || availableProviders.length === 0) {
    return null;
  }

  const displayOrder = buildDisplayOrder(availableProviders, activeProviders);

  /**
   * Toggle a provider on or off in the active voter list.
   * Newly activated providers are appended to the end of the active list.
   */
  const handleToggle = (providerId: RecognitionProvider) => {
    const isCurrentlyActive = activeProviders.includes(providerId);
    if (isCurrentlyActive) {
      onChange(activeProviders.filter((id) => id !== providerId));
    } else {
      onChange([...activeProviders, providerId]);
    }
  };

  /**
   * Move a provider one position earlier in the active priority list.
   */
  const handleMoveUp = (providerId: RecognitionProvider) => {
    const idx = activeProviders.indexOf(providerId);
    if (idx <= 0) return;
    const updated = [...activeProviders];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    onChange(updated);
  };

  /**
   * Move a provider one position later in the active priority list.
   */
  const handleMoveDown = (providerId: RecognitionProvider) => {
    const idx = activeProviders.indexOf(providerId);
    if (idx < 0 || idx >= activeProviders.length - 1) return;
    const updated = [...activeProviders];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    onChange(updated);
  };

  if (displayOrder.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-2">
        No providers available.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {displayOrder.map((provider) => {
        const isActive = activeProviders.includes(provider.id);
        const activeIdx = activeProviders.indexOf(provider.id);
        const isFirst = activeIdx === 0;
        const isLast = activeIdx === activeProviders.length - 1;

        return (
          <ProviderListItem
            key={provider.id}
            provider={provider}
            isActive={isActive}
            isFirst={isFirst}
            isLast={isLast}
            onToggle={() => handleToggle(provider.id)}
            onMoveUp={() => handleMoveUp(provider.id)}
            onMoveDown={() => handleMoveDown(provider.id)}
          />
        );
      })}
    </div>
  );
}
